import {useCallback, useEffect, useMemo, useState} from 'react';
import {format, formatDistanceToNowStrict} from 'date-fns';

import {useQuestionSampleSetDetail} from '../QuestionSampleSets/useQuestionSampleSetDetail';
import {useQuestionMatchingEvaluation} from './useQuestionMatchingEvaluation';
import {StructuredText} from '../../../components/StructuredText';
import {QuestionSampleSetQuestionData} from '../AiSupport.types';
import {IS_PROD} from '~/modules/config';
import {QuestionMatchCandidates} from './QuestionMatchCandidates';

const showAnswer = true;

export const QuestionMatchingEvaluation = ({
  questionSampleSetId,
  onNavToQuestionSampleSet,
}: {
  questionSampleSetId: string;
  onNavToQuestionSampleSet: (id: string) => void;
}) => {
  const now = useState(new Date());
  const {data, sampleData, isLoading, error, fetchData, fetchSampleData} =
    useQuestionSampleSetDetail({
      id: questionSampleSetId,
    });

  const {
    responses,
    onSaveResponse,
    onSubmitResponses,
    submitting,
    error: submitError,
  } = useQuestionMatchingEvaluation({
    questionSampleSetId,
  });

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (data?.sample.data_file_path) {
      fetchSampleData();
    }
  }, [data, fetchSampleData]);

  const [viewList, setViewList] = useState<'responded' | 'unresponded' | 'all'>(
    'all',
  );
  const [inViewQuestion, setInViewQuestion] =
    useState<QuestionSampleSetQuestionData | null>(null);
  const [inViewQuestionMatchIndex, setInViewQuestionMatchIndex] = useState(-1);
  const [inViewQuestionComments, setInViewQuestionComments] =
    useState<string>('');

  const inViewQuestionResponse = inViewQuestion
    ? responses[inViewQuestion.id]
    : null;
  const {responded, unresponded} = useMemo(() => {
    return {
      responded:
        sampleData?.filter(q => {
          return !!responses[q.id];
        }) ?? [],
      unresponded:
        sampleData?.filter(q => {
          return !responses[q.id];
        }) ?? [],
    };
  }, [sampleData, responses]);
  const questionListToView =
    viewList === 'all'
      ? sampleData ?? []
      : viewList === 'responded'
        ? responded
        : unresponded;
  const progress = sampleData ? responded.length / sampleData.length : 0;

  useEffect(() => {
    if (inViewQuestion && responses[inViewQuestion.id]) {
      setInViewQuestionComments(responses[inViewQuestion.id]?.comments ?? '');
      setInViewQuestionMatchIndex(
        responses[inViewQuestion.id]?.best_match_index ?? -1,
      );
    } else {
      setInViewQuestionComments('');
      setInViewQuestionMatchIndex(-1);
    }
  }, [inViewQuestion, responses]);

  const onEscapeKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setInViewQuestion(null);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', onEscapeKeyDown, false);
    return () => {
      document.removeEventListener('keydown', onEscapeKeyDown, false);
    };
  }, [onEscapeKeyDown]);

  if (error) {
    return (
      <div role="alert" className="alert alert-error my-4">
        <span>{error.message}</span>
      </div>
    );
  }

  if (isLoading || !data || !sampleData?.length) {
    return (
      <div className="flex flex-col items-center justify-center p-10">
        <span className="loading loading-dots loading-lg"></span>
        <p>Loading</p>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div>
        <h3 className="text-xl font-semibold">
          Evaluation of sample {questionSampleSetId}
        </h3>
      </div>

      <div className="p-4">
        <button
          className="btn btn-primary"
          disabled={unresponded.length > 0}
          onClick={() => {
            onSubmitResponses({
              onSuccess: () => {
                onNavToQuestionSampleSet(questionSampleSetId);
              },
            });
          }}>
          {submitting ? (
            <span className="loading loading-dots"></span>
          ) : (
            'Submit Evaluation'
          )}
        </button>
        {!!submitError && (
          <div role="alert" className="alert alert-error my-4">
            <span>{submitError.message}</span>
          </div>
        )}
      </div>

      <div>
        <div className="max-w-[500px] mb-6">
          <div className="mb-4">
            <progress
              className="progress progress-primary w-full"
              value={Math.round(progress * 100)}
              max="100"></progress>
          </div>
          <div role="tablist" className="tabs tabs-bordered">
            <a
              role="tab"
              className={'tab ' + (viewList === 'all' ? 'tab-active' : '')}
              onClick={() => {
                setViewList('all');
              }}>
              All ({sampleData.length})
            </a>
            <a
              role="tab"
              className={
                'tab ' + (viewList === 'unresponded' ? 'tab-active' : '')
              }
              onClick={() => {
                setViewList('unresponded');
              }}>
              To Do ({unresponded.length})
            </a>
            <a
              role="tab"
              className={
                'tab ' + (viewList === 'responded' ? 'tab-active' : '')
              }
              onClick={() => {
                setViewList('responded');
              }}>
              Done ({responded.length})
            </a>
          </div>
        </div>

        {questionListToView.map(question => {
          const evaluateQuestionResponse = responses[question.id];
          const bestMatchQuestion =
            evaluateQuestionResponse?.best_match_index >= 0
              ? question.nearest_neighbours[
                  evaluateQuestionResponse.best_match_index
                ]
              : null;
          return (
            <div
              key={question.id}
              className="p-2 my-2 cursor-pointer hover:bg-sky-100 border-b-2 border-slate-100"
              onClick={() => setInViewQuestion(question)}>
              <h5 className="text-lg font-semibold">
                {question.dato_record.question}
              </h5>
              {!!evaluateQuestionResponse && (
                <div className="py-1 ">
                  {bestMatchQuestion ? (
                    <div>
                      <div>
                        <span className="text-green-500">Matched with: </span>
                        <span>{bestMatchQuestion.question.question}</span>
                      </div>
                      <div>
                        Match {evaluateQuestionResponse.best_match_index + 1} of{' '}
                        {question.nearest_neighbours.length} (
                        {(bestMatchQuestion.similarity * 100).toFixed(0)}%)
                      </div>
                    </div>
                  ) : (
                    <div>
                      <span className="text-red-500">
                        No match found in list
                      </span>
                    </div>
                  )}
                  {!!evaluateQuestionResponse.comments && (
                    <div>
                      <small>{evaluateQuestionResponse.comments}</small>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div
        className={'modal ' + (!!inViewQuestion ? 'modal-open' : '')}
        role="dialog">
        {!!inViewQuestion && (
          <div className="modal-box w-11/12 max-w-5xl">
            <div className="mb-4">
              <h3 className="font-bold text-lg mb-2">
                {inViewQuestion.dato_record.question}
              </h3>
              <div>
                <a
                  className="link"
                  href={
                    IS_PROD
                      ? `https://luna-prod.admin.datocms.com/editor/item_types/61134/items/${inViewQuestion.id}`
                      : `https://luna-poc.admin.datocms.com/editor/item_types/730156/items/${inViewQuestion.id}`
                  }
                  target="_blank"
                  rel="noreferrer">
                  {inViewQuestion.id}
                </a>
              </div>
            </div>

            <div>
              <QuestionMatchCandidates
                selectedIndex={inViewQuestionMatchIndex}
                candidates={inViewQuestion.nearest_neighbours}
                onSelectCandidate={setInViewQuestionMatchIndex}
              />

              <div className="my-3">
                <textarea
                  className="textarea textarea-bordered w-full"
                  placeholder="Any additional comments... e.g. why you chose this match?"
                  value={inViewQuestionComments}
                  onChange={e =>
                    setInViewQuestionComments(e.target.value)
                  }></textarea>
              </div>
            </div>
            <div className="modal-action">
              <button
                className="btn"
                type="button"
                onClick={() => {
                  setInViewQuestion(null);
                }}>
                Close
              </button>
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => {
                  onSaveResponse({
                    question_id: inViewQuestion.id,
                    best_match_index: -1,
                    candidates: inViewQuestion.nearest_neighbours.map(
                      n => n.id,
                    ),
                    comments: inViewQuestionComments,
                  });
                  setInViewQuestion(null);
                }}>
                No Match Found
              </button>
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => {
                  onSaveResponse({
                    question_id: inViewQuestion.id,
                    best_match_index: inViewQuestionMatchIndex,
                    candidates: inViewQuestion.nearest_neighbours.map(
                      n => n.id,
                    ),
                    comments: inViewQuestionComments,
                  });
                  setInViewQuestion(null);
                }}>
                Save
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
